import { Field, FormikContext } from "formik";
import React, { Component } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  Label,
  Row,
} from "reactstrap";
import { PaymentRequest } from "./PaymentRequest";
import {
  PaymentRequestV1PaymentMethod,
  ProviderDetails,
  ProviderPaymentMethod,
  RequestedPaymentIdentifiersV1,
  User,
} from "../generated";
import { Mission } from "./AdminCreateMission";
import * as IBAN from "iban";

export interface Props {
  hidden?: boolean;
  providerDetails: ProviderDetails;
  prependPath: string;
  pathResolver?: (obj: any) => any;
  tester: User;
  providerId: string;
}

export interface State {
  providerDetails: ProviderDetails;
  inpaymentDisabled: boolean;
  supportedPaymentMethods: string[];
  currentPaymentMethod: ProviderPaymentMethod;
  isOpenEndedMission: boolean;
  prependPath: string;
  pathResolver: (obj: any) => any;
}

const CustomSelectComponent = (props: any) => (
  <select className="form-control" {...props} />
);

export class PaymentForm extends Component<Props, State> {
  static contextType = FormikContext;

  constructor(props: Props) {
    super(props);

    const supportedPaymentMethods =
      props.providerDetails !== null
        ? Object.keys(this.props.providerDetails.paymentMethods).length > 0
          ? Object.keys(this.props.providerDetails.paymentMethods)
          : ["domestic", "sepa", "inpaymentForm"]
        : null;

    const currentPaymentMethod =
      (supportedPaymentMethods?.length ?? 0) > 0
        ? props.providerDetails.paymentMethods[supportedPaymentMethods[0]] || {}
        : null;

    this.state = {
      inpaymentDisabled: props.prependPath.indexOf("[") >= 0,
      providerDetails: props.providerDetails,
      isOpenEndedMission: props.providerDetails === null,
      supportedPaymentMethods: supportedPaymentMethods,
      currentPaymentMethod: currentPaymentMethod,
      prependPath: props.prependPath,
      pathResolver: props.pathResolver,
    };
  }

  createReference(refType: keyof RequestedPaymentIdentifiersV1) {
    let result = "";

    // Init result with a random number:
    for (let i = 0; i < 8; i++) {
      result += Math.floor(Math.random() * 9) + 1; // 1...9, because a real number can't begin with zero
    }

    function mod97(value: string) {
      let buffer = 0,
        charCode;
      for (let i = 0; i < value.length; i++) {
        charCode = value.charCodeAt(i);
        buffer =
          charCode +
          (charCode >= "A".charCodeAt(0)
            ? buffer * 100 - "A".charCodeAt(0) + 10
            : buffer * 10 - "0".charCodeAt(0));
      }
      return buffer % 97;
    }

    switch (refType) {
      case "creditorReference":
        result =
          "RF" + ("0" + (98 - mod97(result + "RF00"))).slice(-2) + result;
        break;
      case "finnishReference": {
        let reversedRefNumber = result.split("").reverse().join("");
        let checksum = 0;
        for (let i = 0; i < reversedRefNumber.length; i++) {
          checksum +=
            [7, 3, 1][i % 3] * parseInt(reversedRefNumber.charAt(i), 10);
        }
        checksum = 10 - (checksum % 10);
        if (checksum === 10) {
          checksum = 0;
        }
        result = result + checksum;
        break;
      }
      case "endToEndId":
        result = (crypto as any).randomUUID().substring(0, 35);
        break;
      default:
        break;
    }

    return result;
  }

  onPaymentMethodChanged(
    mission: Mission,
    values: PaymentRequest,
    newPaymentMethod: PaymentRequestV1PaymentMethod,
    setFieldTouched: (
      field: string,
      isTouched?: boolean,
      shouldValidate?: boolean
    ) => void
  ) {
    const data = values || this.getPaymentData(mission);
    const newPaymentMethodEnum = newPaymentMethod ?? data.paymentMethod;
    if (newPaymentMethodEnum && !this.state.isOpenEndedMission) {
      const newPaymentMethodSchema =
        this.state.providerDetails.paymentMethods[
          PaymentForm.convertPaymentMethodEnumToPropertyName(
            newPaymentMethodEnum
          )
        ] || {};
      if (this.state.currentPaymentMethod !== newPaymentMethodSchema) {
        this.smartInitializeData(data, newPaymentMethodSchema);

        this.setState({
          currentPaymentMethod: newPaymentMethodSchema,
        });
      }

      for (let fieldPath of Object.keys(
        newPaymentMethodSchema.properties || {}
      )) {
        setFieldTouched(this.state.prependPath + "." + fieldPath, false);
      }
    }
  }

  private static convertPaymentMethodEnumToPropertyName(
    paymentMethod: PaymentRequestV1PaymentMethod
  ) {
    switch (paymentMethod) {
      case PaymentRequestV1PaymentMethod.DOMESTIC:
        return "domestic";
      case PaymentRequestV1PaymentMethod.INPAYMENT_FORM:
        return "inpaymentForm";
      case PaymentRequestV1PaymentMethod.SEPA:
        return "sepa";
      default:
        return null;
    }
  }

  private static convertPropertyNameToPaymentMethodEnum(propertyName: string) {
    switch (propertyName) {
      case "domestic":
        return PaymentRequestV1PaymentMethod.DOMESTIC;
      case "inpaymentForm":
        return PaymentRequestV1PaymentMethod.INPAYMENT_FORM;
      case "sepa":
        return PaymentRequestV1PaymentMethod.SEPA;
      default:
        return null;
    }
  }

  validateField(fieldPath: string, values: PaymentRequest) {
    return (value: string): string | undefined => {
      if (this.props.hidden) {
        return undefined;
      }

      const schema =
        (this.state.currentPaymentMethod?.properties || {})[fieldPath] || {};

      if (fieldPath.endsWith("identifiers.endToEndId")) {
        schema.maxLength = 35;
      }

      if (fieldPath.endsWith("identifiers.creditorReference")) {
        schema.maxLength = 25;
      }

      if (fieldPath.endsWith("identifiers.estonianReference")) {
        schema.maxLength = 20;
      }

      if (fieldPath.endsWith("amount.value")) {
        schema.required = true;
        if (isNaN(Number(schema.greaterThan))) {
          schema.greaterThan = 0;
        }
      }

      if (fieldPath.endsWith("amount.currency")) {
        schema.required = true;
      }

      if (
        schema.required === true &&
        ((typeof value === "string" && (value?.trim()?.length ?? 0) === 0) ||
          (typeof value !== "string" && isNaN(Number(value))))
      ) {
        return "This field is required";
      }

      if (
        schema.minLength !== undefined &&
        (value?.trim()?.length ?? 0) < schema.minLength
      ) {
        return `Minimum length is ${schema.minLength}`;
      }

      if (
        schema.maxLength !== undefined &&
        (value?.trim()?.length ?? 0) > schema.maxLength
      ) {
        return `Maximum length is ${schema.maxLength}`;
      }

      if (
        Array.isArray(schema.possibleValues) &&
        !schema.possibleValues.includes(value?.trim())
      ) {
        return `Possible values are: ${schema.possibleValues.join(", ")}`;
      }

      if (
        schema.greaterThan !== undefined &&
        Number(value) <= schema.greaterThan
      ) {
        return `Value must be greater than ${schema.greaterThan}`;
      }

      if (schema.lessThan !== undefined && Number(value) >= schema.lessThan) {
        return `Value must be less than ${schema.greaterThan}`;
      }

      const countDecimals = (value: number) => {
        if (Math.floor(value) !== value)
          return value.toString().split(".")[1].length || 0;
        return 0;
      };

      if (
        schema.maxDecimals !== undefined &&
        countDecimals(Number(value)) > schema.maxDecimals
      ) {
        return `Maximum number of decimals supported is ${schema.maxDecimals}`;
      }

      if (Array.isArray(schema.rules)) {
        for (let ruleName of schema.rules) {
          const rule = this.state.currentPaymentMethod.rules[ruleName];
          switch (rule.type) {
            case "AtMostOne": {
              const field1Value = PaymentForm.getPropertyValue(
                values,
                rule.fields[0]
              );
              const field2Value = PaymentForm.getPropertyValue(
                values,
                rule.fields[1]
              );
              if (field1Value && field2Value) {
                return `Only one of these properties can be set at the same time: ${rule.fields[0]}, ${rule.fields[1]}`;
              }
              break;
            }
          }
        }
      }

      if (fieldPath.endsWith("destination.iban.ibanNumber")) {
        if ((value?.trim() ?? "") && !IBAN.isValid(value)) {
          return "IBAN is invalid";
        }
      }

      return undefined;
    };
  }

  getFieldContext(fieldPath: string, callback: (description?: string) => any) {
    if (
      this.state.isOpenEndedMission ||
      !this.state.currentPaymentMethod.properties
    ) {
      return callback(null);
    }

    if (!this.state.currentPaymentMethod) {
      return;
    }

    let prop = this.state.currentPaymentMethod.properties[fieldPath];
    if (prop) {
      return callback(prop.description);
    }

    if (
      fieldPath.endsWith(".*") &&
      Object.keys(this.state.currentPaymentMethod.properties).filter((x) =>
        x.startsWith(fieldPath.substring(0, fieldPath.length - 1))
      ).length > 0
    ) {
      return callback(null);
    }
  }

  render() {
    const { setFieldValue, setFieldTouched } = this.context;

    const errors = this.resolveFormPath(this.context.errors) || {};
    const touched = this.resolveFormPath(this.context.touched) || {};
    const values = (this.resolveFormPath(this.context.values) ||
      {}) as PaymentRequest;

    if (!values.paymentMethod && this.state.currentPaymentMethod) {
      values.paymentMethod = PaymentForm.convertPropertyNameToPaymentMethodEnum(
        this.state.supportedPaymentMethods[0]
      );

      this.smartInitializeData(values, this.state.currentPaymentMethod);
    } else if (this.state.isOpenEndedMission && !values.paymentMethod) {
      values.amount.currency = "EUR";
      values.message = "";
      values.transactionText = "";
      values.identifiers.ocr = "";
      values.identifiers.endToEndId = "";
      values.identifiers.creditorReference = "";
      values.identifiers.estonianReference = "";
      values.identifiers.finnishReference = "";
      values.identifiers.norwegianKid = "";
      values.paymentMethod = PaymentRequestV1PaymentMethod.DOMESTIC;
      values.destination.name = "";
      values.destination.iban = {
        ibanNumber: "",
      };
      values.destination.bban = {
        bankCode: "",
        accountNumber: "",
      };
      values.destination.address = {
        city: "",
        buildingNumber: "",
        country: "",
        postalCode: "",
        street: "",
      };
    }

    return (
      <Container hidden={!!this.props.hidden}>
        <Row>
          <Col xs={12}>
            {!this.state.isOpenEndedMission && (
              <Row className={"mb-3"}>
                <ButtonGroup className={"w-100"}>
                  {this.state.supportedPaymentMethods.indexOf("domestic") >=
                    0 && (
                    <Button
                      color="primary"
                      onClick={() => {
                        setFieldValue(
                          this.state.prependPath + ".paymentMethod",
                          PaymentRequestV1PaymentMethod.DOMESTIC
                        );
                        this.onPaymentMethodChanged(
                          null,
                          values,
                          PaymentRequestV1PaymentMethod.DOMESTIC,
                          setFieldTouched
                        );
                      }}
                      outline={
                        values.paymentMethod !==
                        PaymentRequestV1PaymentMethod.DOMESTIC
                      }
                    >
                      Domestic
                    </Button>
                  )}
                  {this.state.supportedPaymentMethods.indexOf("sepa") >= 0 && (
                    <Button
                      color="primary"
                      onClick={() => {
                        setFieldValue(
                          this.state.prependPath + ".paymentMethod",
                          PaymentRequestV1PaymentMethod.SEPA
                        );
                        this.onPaymentMethodChanged(
                          null,
                          values,
                          PaymentRequestV1PaymentMethod.SEPA,
                          setFieldTouched
                        );
                      }}
                      outline={
                        values.paymentMethod !==
                        PaymentRequestV1PaymentMethod.SEPA
                      }
                    >
                      SEPA
                    </Button>
                  )}
                  {this.state.supportedPaymentMethods.indexOf(
                    "inpaymentForm"
                  ) >= 0 && (
                    <Button
                      color="primary"
                      onClick={() => {
                        setFieldValue(
                          this.state.prependPath + ".paymentMethod",
                          PaymentRequestV1PaymentMethod.INPAYMENT_FORM
                        );
                        this.onPaymentMethodChanged(
                          null,
                          values,
                          PaymentRequestV1PaymentMethod.INPAYMENT_FORM,
                          setFieldTouched
                        );
                      }}
                      outline={
                        values.paymentMethod !==
                        PaymentRequestV1PaymentMethod.INPAYMENT_FORM
                      }
                    >
                      Inpayment Form
                    </Button>
                  )}
                </ButtonGroup>
              </Row>
            )}
          </Col>
        </Row>

        {(this.state.isOpenEndedMission || this.state.currentPaymentMethod) && (
          <Row>
            <Col xs={12} md={6}>
              <h4>Basics</h4>
              <Row>
                <Col xs={9}>
                  {this.getFieldContext("amount.value", (fieldDescription) => (
                    <FormGroup>
                      <Label htmlFor={this.state.prependPath + `.amount.value`}>
                        Amount
                      </Label>
                      <Input
                        name={this.state.prependPath + ".amount.value"}
                        placeholder="1.23"
                        tag={Field}
                        validate={this.validateField("amount.value", values)}
                        valid={!errors.amount?.value && touched.amount?.value}
                        invalid={errors.amount?.value && touched.amount?.value}
                      />

                      {errors.amount?.value && touched.amount?.value && (
                        <FormFeedback>{errors.amount?.value}</FormFeedback>
                      )}
                    </FormGroup>
                  ))}
                </Col>
                <Col xs={3}>
                  {this.getFieldContext(
                    "amount.currency",
                    (fieldDescription) => (
                      <FormGroup>
                        <Label
                          htmlFor={this.state.prependPath + `.amount.currency`}
                        >
                          Currency
                        </Label>
                        <Input
                          name={this.state.prependPath + ".amount.currency"}
                          placeholder="EUR"
                          type="text"
                          tag={Field}
                          validate={this.validateField(
                            "amount.currency",
                            values
                          )}
                          valid={
                            !errors.amount?.currency && touched.amount?.currency
                          }
                          invalid={
                            errors.amount?.currency && touched.amount?.currency
                          }
                        />
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.amount?.currency &&
                          touched.amount?.currency && (
                            <FormFeedback>
                              {errors.amount?.currency}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    )
                  )}
                </Col>
              </Row>
              {this.getFieldContext("execution.type", (fieldDescription) => (
                <Row>
                  <Col
                    xs={12}
                    md={values.execution?.type === "SpecificDate" ? 6 : 12}
                  >
                    <FormGroup>
                      <Label
                        htmlFor={this.state.prependPath + `execution.type`}
                      >
                        Execution Type
                      </Label>
                      <Field
                        name={this.state.prependPath + ".execution.type"}
                        as={CustomSelectComponent}
                        validate={this.validateField("execution.type", values)}
                        valid={(!!(
                          !errors.execution?.type && touched.execution?.type
                        )).toString()}
                        invalid={(!!(
                          errors.execution?.type && touched.execution?.type
                        )).toString()}
                      >
                        {(this.state.isOpenEndedMission ||
                          (this.state.currentPaymentMethod.executionTypes?.indexOf(
                            "Normal"
                          ) ?? 0) >= 0) && (
                          <option value={"Normal"}>Normal</option>
                        )}
                        {(this.state.isOpenEndedMission ||
                          (this.state.currentPaymentMethod.executionTypes?.indexOf(
                            "Instant"
                          ) ?? 0) >= 0) && (
                          <option value={"Instant"}>Instant</option>
                        )}
                        {(this.state.isOpenEndedMission ||
                          (this.state.currentPaymentMethod.executionTypes?.indexOf(
                            "SpecificDate"
                          ) ?? 0) >= 0) && (
                          <option value={"SpecificDate"}>Specific date</option>
                        )}
                      </Field>
                      {fieldDescription && (
                        <FormText color="muted"> {fieldDescription}</FormText>
                      )}
                      {errors.execution?.type && touched.execution?.type && (
                        <FormFeedback>{errors.execution?.type}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6}>
                    {values.execution?.type === "SpecificDate" &&
                      this.getFieldContext(
                        "execution.date",
                        (fieldDescription) => (
                          <FormGroup>
                            <Label
                              htmlFor={
                                this.state.prependPath + `execution.date`
                              }
                            >
                              Execution Date
                            </Label>
                            <Input
                              name={this.state.prependPath + ".execution.date"}
                              type="date"
                              tag={Field}
                              validate={this.validateField(
                                "execution.date",
                                values
                              )}
                              valid={
                                !errors.execution?.date &&
                                touched.execution?.date
                              }
                              invalid={
                                errors.execution?.date &&
                                touched.execution?.date
                              }
                            />
                            {fieldDescription && (
                              <FormText color="muted">
                                {fieldDescription}
                              </FormText>
                            )}
                            {errors.execution?.date &&
                              touched.execution?.date && (
                                <FormFeedback>
                                  {errors.execution?.date}
                                </FormFeedback>
                              )}
                          </FormGroup>
                        )
                      )}
                  </Col>
                </Row>
              ))}

              <Col xs={12} md={12}>
                {this.getFieldContext("transactionText", (fieldDescription) => (
                  <FormGroup>
                    <Label htmlFor={this.state.prependPath + `transactionText`}>
                      Transaction Text
                    </Label>
                    <Input
                      name={this.state.prependPath + ".transactionText"}
                      placeholder="Paying invoice ID148773"
                      type={"text"}
                      tag={Field}
                      validate={this.validateField("transactionText", values)}
                      valid={!errors.transactionText && touched.transactionText}
                      invalid={
                        errors.transactionText && touched.transactionText
                      }
                    />
                    <FormText color="muted">
                      {fieldDescription || "Message on own statement"}
                    </FormText>
                    {errors.transactionText && touched.transactionText && (
                      <FormFeedback>{errors.transactionText}</FormFeedback>
                    )}
                  </FormGroup>
                ))}

                {this.getFieldContext("message", (fieldDescription) => (
                  <FormGroup>
                    <Label htmlFor={this.state.prependPath + `message`}>
                      Message
                    </Label>
                    <Input
                      name={this.state.prependPath + ".message"}
                      placeholder="Invoice ID148773"
                      type={"text"}
                      tag={Field}
                      validate={this.validateField("message", values)}
                      valid={!errors.message && touched.message}
                      invalid={errors.message && touched.message}
                    />
                    <FormText color="muted">
                      {fieldDescription || "Message to recipient"}
                    </FormText>
                    {errors.message && touched.message && (
                      <FormFeedback>{errors.message}</FormFeedback>
                    )}
                  </FormGroup>
                ))}
              </Col>
            </Col>
            <Col xs={12} md={6}>
              <h4>Destination</h4>
              <Row>
                {this.getFieldContext(
                  "destination.name",
                  (fieldDescription) => (
                    <Col>
                      <FormGroup>
                        <Label
                          htmlFor={this.state.prependPath + `destination.name`}
                        >
                          Name
                        </Label>
                        <Input
                          name={this.state.prependPath + ".destination.name"}
                          placeholder="John Doe"
                          type="text"
                          tag={Field}
                          validate={this.validateField(
                            "destination.name",
                            values
                          )}
                          valid={
                            !errors.destination?.name &&
                            touched.destination?.name
                          }
                          invalid={
                            errors.destination?.name &&
                            touched.destination?.name
                          }
                        />
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.destination?.name &&
                          touched.destination?.name && (
                            <FormFeedback>
                              {errors.destination?.name}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  )
                )}
              </Row>

              <Row>
                {this.getFieldContext(
                  "destination.bban",
                  (fieldDescription) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath + `destination.bban.bankCode`
                        }
                      >
                        BBAN
                      </Label>
                      <Row>
                        <Col sm="3">
                          <Input
                            name={
                              this.state.prependPath +
                              ".destination.bban.bankCode"
                            }
                            placeholder="5041"
                            type="text"
                            tag={Field}
                            validate={this.validateField(
                              "destination.bban.bankCode",
                              values
                            )}
                            valid={
                              !errors.destination?.bban?.bankCode &&
                              touched.destination?.bban?.bankCode
                            }
                            invalid={
                              errors.destination?.bban?.bankCode &&
                              touched.destination?.bban?.bankCode
                            }
                          />
                        </Col>
                        <Col>
                          <Input
                            name={
                              this.state.prependPath +
                              ".destination.bban.accountNumber"
                            }
                            placeholder="000102775"
                            type="text"
                            tag={Field}
                            validate={this.validateField(
                              "destination.bban.accountNumber",
                              values
                            )}
                            valid={
                              !errors.destination?.bban?.accountNumber &&
                              touched.destination?.bban?.accountNumber
                            }
                            invalid={
                              errors.destination?.bban?.accountNumber &&
                              touched.destination?.bban?.accountNumber
                            }
                          />
                        </Col>
                      </Row>
                      <FormText color="muted">
                        {fieldDescription ||
                          "BBAN (local account number) of the recipient"}
                      </FormText>
                      {errors.destination?.bban?.bankCode &&
                        touched.destination?.bban?.bankCode && (
                          <FormFeedback>
                            {errors.destination?.bban?.bankCode}
                          </FormFeedback>
                        )}
                      {errors.destination?.bban?.accountNumber &&
                        touched.destination?.bban?.accountNumber && (
                          <FormFeedback>
                            {errors.destination?.bban?.accountNumber}
                          </FormFeedback>
                        )}
                    </FormGroup>
                  )
                )}
              </Row>

              <Row>
                {this.getFieldContext(
                  "destination.iban",
                  (fieldDescription) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath + `destination.iban.ibanNumber`
                        }
                      >
                        IBAN
                      </Label>
                      {this.props.tester?.banks &&
                        this.props.tester?.banks.filter(
                          (x) => x.iban && x.id !== this.props.providerId
                        ).length > 0 && (
                          <Field
                            name={
                              this.state.prependPath +
                              ".destination.iban.ibanNumber"
                            }
                            as={CustomSelectComponent}
                            validate={this.validateField(
                              "destination.iban.ibanNumber",
                              values
                            )}
                            valid={(!!(
                              !errors.destination?.iban?.ibanNumber &&
                              touched.destination?.iban?.ibanNumber
                            )).toString()}
                            invalid={(!!(
                              errors.destination?.iban?.ibanNumber &&
                              touched.destination?.iban?.ibanNumber
                            )).toString()}
                          >
                            <option value="" />
                            {this.props.tester.banks
                              .filter(
                                (x) => x.iban && x.id !== this.props.providerId
                              )
                              .map((x) => (
                                <option
                                  key={this.state.prependPath + x.iban}
                                  value={x.iban}
                                >
                                  {x.displayName} ({x.iban})
                                </option>
                              ))}
                          </Field>
                        )}
                      {(!this.props.tester?.banks ||
                        this.props.tester?.banks.filter(
                          (x) => x.iban && x.id !== this.props.providerId
                        ).length === 0) && (
                        <Input
                          name={
                            this.state.prependPath +
                            ".destination.iban.ibanNumber"
                          }
                          placeholder="DK27005041000102775"
                          type="text"
                          tag={Field}
                          validate={this.validateField(
                            "destination.iban.ibanNumber",
                            values
                          )}
                          valid={
                            !errors.destination?.iban?.ibanNumber &&
                            touched.destination?.iban?.ibanNumber
                          }
                          invalid={
                            errors.destination?.iban?.ibanNumber &&
                            touched.destination?.iban?.ibanNumber
                          }
                        />
                      )}
                      <FormText color="muted">
                        {fieldDescription ||
                          "IBAN (international account number) of the recipient"}
                      </FormText>
                      {errors.destination?.iban?.ibanNumber &&
                        touched.destination?.iban?.ibanNumber && (
                          <FormFeedback>
                            {errors.destination?.iban?.ibanNumber}
                          </FormFeedback>
                        )}
                    </FormGroup>
                  )
                )}
              </Row>

              <Row>
                {!this.state.inpaymentDisabled &&
                  this.getFieldContext("destination.inpaymentForm.*", (_) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath +
                          `destination.inpaymentForm.type`
                        }
                      >
                        Inpayment Form (Giro payment)
                      </Label>
                      <Row>
                        <Col sm="3">
                          <Field
                            name={
                              this.state.prependPath +
                              ".destination.inpaymentForm.type"
                            }
                            as={CustomSelectComponent}
                            validate={this.validateField(
                              "destination.inpaymentForm.type",
                              values
                            )}
                            valid={(!!(
                              !errors.destination?.inpaymentForm?.type &&
                              touched.destination?.inpaymentForm?.type
                            )).toString()}
                            invalid={(!!(
                              errors.destination?.inpaymentForm?.type &&
                              touched.destination?.inpaymentForm?.type
                            )).toString()}
                          >
                            <option value={""} />
                            <option value={"SE_PGNR"}>SE_PGNR</option>
                            <option value={"SE_BGNR"}>SE_BGNR</option>
                            <option value={"DK_G01"}>DK_G01</option>
                            <option value={"DK_G04"}>DK_G04</option>
                            <option value={"DK_G15"}>DK_G15</option>
                            <option value={"DK_FI71"}>DK_FI71</option>
                            <option value={"DK_FI73"}>DK_FI73</option>
                            <option value={"DK_FI75"}>DK_FI75</option>
                          </Field>
                          <FormText color="muted">Form type</FormText>
                          {errors.destination?.inpaymentForm?.type &&
                            touched.destination?.inpaymentForm?.type && (
                              <FormFeedback>
                                {errors.destination?.inpaymentForm?.type}
                              </FormFeedback>
                            )}
                        </Col>
                        <Col>
                          <Input
                            name={
                              this.state.prependPath +
                              ".destination.inpaymentForm.creditorNumber"
                            }
                            placeholder="000102775"
                            type="text"
                            tag={Field}
                            validate={this.validateField(
                              "destination.inpaymentForm.creditorNumber",
                              values
                            )}
                            valid={
                              !errors.destination?.inpaymentForm
                                ?.creditorNumber &&
                              touched.destination?.inpaymentForm?.creditorNumber
                            }
                            invalid={
                              errors.destination?.inpaymentForm
                                ?.creditorNumber &&
                              touched.destination?.inpaymentForm?.creditorNumber
                            }
                          />
                          <FormText color="muted">Creditor number</FormText>
                          {errors.destination?.inpaymentForm?.creditorNumber &&
                            touched.destination?.inpaymentForm
                              ?.creditorNumber && (
                              <FormFeedback>
                                {
                                  errors.destination?.inpaymentForm
                                    ?.creditorNumber
                                }
                              </FormFeedback>
                            )}
                        </Col>
                      </Row>
                    </FormGroup>
                  ))}
              </Row>

              <Row>
                {this.getFieldContext(
                  "destination.address.street",
                  (fieldDescription) => (
                    <Col xs={12} md={8}>
                      <FormGroup>
                        <Label
                          htmlFor={
                            this.state.prependPath +
                            `destination.address.street`
                          }
                        >
                          Street
                        </Label>
                        <Input
                          name={
                            this.state.prependPath +
                            ".destination.address.street"
                          }
                          placeholder="Artillerivej"
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "destination.address.street",
                            values
                          )}
                          valid={
                            !errors.destination?.address?.street &&
                            touched.destination?.address?.street
                          }
                          invalid={
                            errors.destination?.address?.street &&
                            touched.destination?.address?.street
                          }
                        />
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.destination?.address?.street &&
                          touched.destination?.address?.street && (
                            <FormFeedback>
                              {errors.destination?.address?.street}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  )
                )}

                {this.getFieldContext(
                  "destination.address.buildingNumber",
                  (fieldDescription) => (
                    <Col xs={12} md={4}>
                      <FormGroup>
                        <Label
                          htmlFor={
                            this.state.prependPath +
                            `destination.address.buildingNumber`
                          }
                        >
                          Building no.
                        </Label>
                        <Input
                          name={
                            this.state.prependPath +
                            ".destination.address.buildingNumber"
                          }
                          placeholder="86"
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "destination.address.buildingNumber",
                            values
                          )}
                          valid={
                            !errors.destination?.address?.buildingNumber &&
                            touched.destination?.address?.buildingNumber
                          }
                          invalid={
                            errors.destination?.address?.buildingNumber &&
                            touched.destination?.address?.buildingNumber
                          }
                        />
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.destination?.address?.buildingNumber &&
                          touched.destination?.address?.buildingNumber && (
                            <FormFeedback>
                              {errors.destination?.address?.buildingNumber}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  )
                )}
              </Row>

              <Row>
                {this.getFieldContext(
                  "destination.address.postalCode",
                  (fieldDescription) => (
                    <Col xs={12} md={4}>
                      <FormGroup>
                        <Label
                          htmlFor={
                            this.state.prependPath +
                            `destination.address.postalCode`
                          }
                        >
                          Postal code
                        </Label>
                        <Input
                          name={
                            this.state.prependPath +
                            ".destination.address.postalCode"
                          }
                          placeholder="2300"
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "destination.address.postalCode",
                            values
                          )}
                          valid={
                            !errors.destination?.address?.postalCode &&
                            touched.destination?.address?.postalCode
                          }
                          invalid={
                            errors.destination?.address?.postalCode &&
                            touched.destination?.address?.postalCode
                          }
                        />
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.destination?.address?.postalCode &&
                          touched.destination?.address?.postalCode && (
                            <FormFeedback>
                              {errors.destination?.address?.postalCode}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  )
                )}

                {this.getFieldContext(
                  "destination.address.city",
                  (fieldDescription) => (
                    <Col xs={12} md={4}>
                      <FormGroup>
                        <Label
                          htmlFor={
                            this.state.prependPath + `destination.address.city`
                          }
                        >
                          City
                        </Label>
                        <Input
                          name={
                            this.state.prependPath + ".destination.address.city"
                          }
                          placeholder="Copenhagen"
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "destination.address.city",
                            values
                          )}
                          valid={
                            !errors.destination?.address?.city &&
                            touched.destination?.address?.city
                          }
                          invalid={
                            errors.destination?.address?.city &&
                            touched.destination?.address?.city
                          }
                        />
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.destination?.address?.city &&
                          touched.destination?.address?.city && (
                            <FormFeedback>
                              {errors.destination?.address?.city}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  )
                )}

                {this.getFieldContext(
                  "destination.address.country",
                  (fieldDescription) => (
                    <Col xs={12} md={4}>
                      <FormGroup>
                        <Label
                          htmlFor={
                            this.state.prependPath +
                            `destination.address.country`
                          }
                        >
                          Country code
                        </Label>
                        <Input
                          name={
                            this.state.prependPath +
                            ".destination.address.country"
                          }
                          placeholder="DK"
                          type={"text"}
                          tag={Field}
                          maxLength={2}
                          validate={this.validateField(
                            "destination.address.country",
                            values
                          )}
                          valid={
                            !errors.destination?.address?.country &&
                            touched.destination?.address?.country
                          }
                          invalid={
                            errors.destination?.address?.country &&
                            touched.destination?.address?.country
                          }
                        />
                        <FormText color="muted">
                          {fieldDescription || "2-letter code"}
                        </FormText>
                        {errors.destination?.address?.country &&
                          touched.destination?.address?.country && (
                            <FormFeedback>
                              {errors.destination?.address?.country}
                            </FormFeedback>
                          )}
                      </FormGroup>
                    </Col>
                  )
                )}
              </Row>
            </Col>
          </Row>
        )}

        {(this.state.isOpenEndedMission || this.state.currentPaymentMethod) &&
          this.getFieldContext("identifiers.*", (_) => (
            <Row>
              <Col xs={12} md={6}>
                <h4>Identifiers</h4>

                {this.getFieldContext(
                  "identifiers.endToEndId",
                  (fieldDescription) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath + `identifiers.endToEndId`
                        }
                      >
                        End-to-End Id
                      </Label>
                      <InputGroup>
                        <Input
                          name={
                            this.state.prependPath + ".identifiers.endToEndId"
                          }
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "identifiers.endToEndId",
                            values
                          )}
                          valid={
                            !errors.identifiers?.endToEndId &&
                            touched.identifiers?.endToEndId
                          }
                          invalid={
                            errors.identifiers?.endToEndId &&
                            touched.identifiers?.endToEndId
                          }
                        />
                        <Button
                          inline="inline"
                          onClick={() =>
                            setFieldValue(
                              this.state.prependPath +
                                ".identifiers.endToEndId",
                              this.createReference("endToEndId")
                            )
                          }
                        >
                          +
                        </Button>
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.identifiers?.endToEndId &&
                          touched.identifiers?.endToEndId && (
                            <FormFeedback>
                              {errors.identifiers?.endToEndId}
                            </FormFeedback>
                          )}
                      </InputGroup>
                    </FormGroup>
                  )
                )}
                {this.getFieldContext(
                  "identifiers.creditorReference",
                  (fieldDescription) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath +
                          `identifiers.creditorReference`
                        }
                      >
                        Creditor Reference
                      </Label>
                      <InputGroup>
                        <Input
                          name={
                            this.state.prependPath +
                            ".identifiers.creditorReference"
                          }
                          pattern="^RF[A-Z0-9]{2,23}$"
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "identifiers.creditorReference",
                            values
                          )}
                          valid={
                            !errors.identifiers?.creditorReference &&
                            touched.identifiers?.creditorReference
                          }
                          invalid={
                            errors.identifiers?.creditorReference &&
                            touched.identifiers?.creditorReference
                          }
                        />
                        <Button
                          inline="inline"
                          onClick={() =>
                            setFieldValue(
                              this.state.prependPath +
                                ".identifiers.creditorReference",
                              this.createReference("creditorReference")
                            )
                          }
                        >
                          +
                        </Button>
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.identifiers?.creditorReference &&
                          touched.identifiers?.creditorReference && (
                            <FormFeedback>
                              {errors.identifiers?.creditorReference}
                            </FormFeedback>
                          )}
                      </InputGroup>
                    </FormGroup>
                  )
                )}
                {this.getFieldContext(
                  "identifiers.estonianReference",
                  (fieldDescription) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath +
                          `identifiers.estonianReference`
                        }
                      >
                        Estonian Reference
                      </Label>
                      <InputGroup>
                        <Input
                          name={
                            this.state.prependPath +
                            ".identifiers.estonianReference"
                          }
                          pattern="^\d{2,20}$"
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "identifiers.estonianReference",
                            values
                          )}
                          valid={
                            !errors.identifiers?.estonianReference &&
                            touched.identifiers?.estonianReference
                          }
                          invalid={
                            errors.identifiers?.estonianReference &&
                            touched.identifiers?.estonianReference
                          }
                        />
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.identifiers?.estonianReference &&
                          touched.identifiers?.estonianReference && (
                            <FormFeedback>
                              {errors.identifiers?.estonianReference}
                            </FormFeedback>
                          )}
                      </InputGroup>
                    </FormGroup>
                  )
                )}
                {this.getFieldContext("identifiers.ocr", (fieldDescription) => (
                  <FormGroup>
                    <Label htmlFor={this.state.prependPath + `identifiers.ocr`}>
                      OCR
                    </Label>
                    <Input
                      name={this.state.prependPath + ".identifiers.ocr"}
                      type={"text"}
                      tag={Field}
                      validate={this.validateField("identifiers.ocr", values)}
                      valid={
                        !errors.identifiers?.ocr && touched.identifiers?.ocr
                      }
                      invalid={
                        errors.identifiers?.ocr && touched.identifiers?.ocr
                      }
                    />
                    {fieldDescription && (
                      <FormText color="muted">{fieldDescription}</FormText>
                    )}
                    {errors.identifiers?.ocr && touched.identifiers?.ocr && (
                      <FormFeedback>{errors.identifiers?.ocr}</FormFeedback>
                    )}
                  </FormGroup>
                ))}
                {this.getFieldContext(
                  "identifiers.finnishReference",
                  (fieldDescription) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath +
                          `identifiers.finnishReference`
                        }
                      >
                        Finnish Reference
                      </Label>
                      <InputGroup>
                        <Input
                          name={
                            this.state.prependPath +
                            ".identifiers.finnishReference"
                          }
                          type={"text"}
                          tag={Field}
                          validate={this.validateField(
                            "identifiers.finnishReference",
                            values
                          )}
                          valid={
                            !errors.identifiers?.finnishReference &&
                            touched.identifiers?.finnishReference
                          }
                          invalid={
                            errors.identifiers?.finnishReference &&
                            touched.identifiers?.finnishReference
                          }
                        />
                        <Button
                          inline="inline"
                          onClick={() =>
                            setFieldValue(
                              this.state.prependPath +
                                ".identifiers.finnishReference",
                              this.createReference("finnishReference")
                            )
                          }
                        >
                          +
                        </Button>
                        {fieldDescription && (
                          <FormText color="muted">{fieldDescription}</FormText>
                        )}
                        {errors.identifiers?.finnishReference &&
                          touched.identifiers?.finnishReference && (
                            <FormFeedback>
                              {errors.identifiers?.finnishReference}
                            </FormFeedback>
                          )}
                      </InputGroup>
                    </FormGroup>
                  )
                )}
                {this.getFieldContext(
                  "identifiers.norwegianKid",
                  (fieldDescription) => (
                    <FormGroup>
                      <Label
                        htmlFor={
                          this.state.prependPath + `.identifiers.norwegianKid`
                        }
                      >
                        Norwegian KID
                      </Label>
                      <Input
                        name={
                          this.state.prependPath + ".identifiers.norwegianKid"
                        }
                        type={"text"}
                        tag={Field}
                        validate={this.validateField(
                          "identifiers.norwegianKid",
                          values
                        )}
                        valid={
                          !errors.identifiers?.norwegianKid &&
                          touched.identifiers?.norwegianKid
                        }
                        invalid={
                          errors.identifiers?.norwegianKid &&
                          touched.identifiers?.norwegianKid
                        }
                      />
                      {fieldDescription && (
                        <FormText color="muted">{fieldDescription}</FormText>
                      )}
                      {errors.identifiers?.norwegianKid &&
                        touched.identifiers?.norwegianKid && (
                          <FormFeedback>
                            {errors.identifiers?.norwegianKid}
                          </FormFeedback>
                        )}
                    </FormGroup>
                  )
                )}
              </Col>
            </Row>
          ))}
      </Container>
    );
  }

  private smartInitializeData(
    data: PaymentRequest,
    paymentMethodSchema: ProviderPaymentMethod
  ) {
    if (paymentMethodSchema.properties) {
      for (let propertyPath of Object.keys(paymentMethodSchema.properties)) {
        const propertyAccess = PaymentForm.getPropertyAccess(
          data,
          propertyPath
        );
        const schema = paymentMethodSchema.properties[propertyPath];
        if (
          propertyAccess.obj[propertyAccess.propertyName] === undefined ||
          propertyAccess.obj[propertyAccess.propertyName] === null
        ) {
          if (
            Array.isArray(schema.possibleValues) &&
            schema.possibleValues.length === 1
          ) {
            propertyAccess.obj[propertyAccess.propertyName] =
              schema.possibleValues[0];
          } else if (schema.greaterThan !== undefined) {
            propertyAccess.obj[propertyAccess.propertyName] =
              schema.greaterThan;
          } else {
            propertyAccess.obj[propertyAccess.propertyName] = "";
          }
        }
      }

      if (
        this.props.tester &&
        paymentMethodSchema.properties["destination.name"]
      ) {
        data.destination.name = `${this.props.tester.firstName} ${this.props.tester.lastName}`;
      }
    }
  }

  private static getPropertyAccess(data: PaymentRequest, propertyPath: string) {
    const lastIndexOfDot = propertyPath.lastIndexOf(".");
    if (lastIndexOfDot < 0) {
      return {
        obj: data,
        propertyName: propertyPath,
      };
    }

    const propertyNames = propertyPath.substring(0, lastIndexOfDot).split(".");
    let currentObj = data as any;
    for (let propertyName of propertyNames) {
      if (!currentObj[propertyName]) {
        currentObj[propertyName] = {};
      }
      currentObj = currentObj[propertyName];
    }

    return {
      obj: currentObj,
      propertyName: propertyPath.substring(lastIndexOfDot + 1),
    };
  }

  private static getPropertyValue(data: PaymentRequest, propertyPath: string) {
    const propertyAccess = PaymentForm.getPropertyAccess(data, propertyPath);
    return propertyAccess.obj[propertyAccess.propertyName];
  }

  private resolveFormPath(obj: any): any {
    try {
      if (this.state.prependPath.indexOf("[") < 0) {
        return obj[this.state.prependPath];
      }

      return this.state.pathResolver(obj);
    } catch {
      return null;
    }
  }

  private getPaymentData(mission: Mission): PaymentRequest {
    return this.resolveFormPath(mission) as PaymentRequest;
  }
}
